/*
 * VerqoSmartShippingBar — inline widget that surfaces 3 advanced features
 * of Verqo Shipping (Insurance + Luma AI Smart Rate + USPS Zone) alongside
 * the buyer's standard rate-shop / book flow.
 *
 * Auto-shows/hides sections based on eligibility:
 *   · Zone chip: shown once BOTH origin + destination ZIPs are set
 *   · Smart Rate: shown once ALL shipment details (ZIP + parcel) are set,
 *     and hidden if the platform hasn't configured a Luma ruleset yet
 *   · Insurance: only shown on the post-book success card (needs a
 *     shipment id from the just-booked label)
 *
 * Props:
 *   mode            — "quote" (Step 2, pre-rates) | "booked" (Step 4, post-book)
 *   origin, dest    — {zip, state, city}
 *   parcel          — {length, width, height, weight}
 *   shipmentId      — EP shipment id (only when mode==="booked")
 *   declaredValue   — helpful default for insurance amount
 *   onInsured       — callback after successful insurance-add
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, MapPin, Shield, Loader2, TrendingDown, Clock } from "lucide-react";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";


export default function VerqoSmartShippingBar({
  mode = "quote", origin, dest, parcel,
  shipmentId, trackingCode, carrier, reference,
  declaredValue = 500, onInsured,
}) {
  const [zone, setZone]           = useState(null);
  const [zoneLoading, setZLoad]   = useState(false);
  const [smart, setSmart]         = useState(null);
  const [smartLoading, setSLoad]  = useState(false);
  const [ruleset, setRuleset]     = useState(null); // { ruleset_name, validated }
  const [insAmount, setInsAmount] = useState(String(declaredValue || 500));
  const [insLoading, setInsLoad]  = useState(false);
  const [insDone, setInsDone]     = useState(false);

  const dev = typeof origin === "object" && origin?.zip && dest?.zip;

  const buildAddr = (a, defaults = {}) => ({
    name:     a.name    || defaults.name    || "Shipper",
    street1:  a.street1 || a.address1 || defaults.street1 || "417 Montgomery St",
    city:     a.city    || defaults.city    || "San Francisco",
    state:    a.state   || defaults.state   || "CA",
    zip:      a.zip     || defaults.zip     || "94104",
    country:  a.country || "US",
  });

  // Load platform default Luma ruleset name once.
  useEffect(() => {
    if (mode !== "quote") return;
    api.get("/dropship/verqo-shipping/luma-config")
       .then(r => setRuleset(r.data))
       .catch(() => setRuleset({ ruleset_name: "", validated: false }));
  }, [mode]);

  // Auto-fetch USPS zone as soon as we have both ZIPs.
  useEffect(() => {
    if (mode !== "quote" || !dev) return;
    let cancelled = false;
    (async () => {
      setZLoad(true);
      try {
        const { data } = await api.post("/dropship/verqo-shipping/usps-zone", {
          ship_from: buildAddr(origin, { name: "Origin" }),
          ship_to:   buildAddr(dest,   { name: "Destination" }),
          parcel:    parcel || { length: 10, width: 8, height: 4, weight: 16 },
        });
        if (!cancelled) setZone(data.usps_zone);
      } catch {
        if (!cancelled) setZone(null);
      } finally {
        if (!cancelled) setZLoad(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, origin?.zip, dest?.zip, origin, dest, parcel]);

  const runSmartRate = useCallback(async () => {
    if (!ruleset?.ruleset_name) {
      toast.error("Verqo Smart Rate is not configured yet — ask your admin to set the AI ruleset");
      return;
    }
    setSLoad(true);
    try {
      const { data } = await api.post("/dropship/verqo-shipping/smart-rate/promise", {
        ship_from:         buildAddr(origin),
        ship_to:           buildAddr(dest),
        parcel:            parcel || { length: 10, width: 8, height: 4, weight: 16 },
        ruleset_name:      ruleset.ruleset_name,
        planned_ship_date: new Date().toISOString().slice(0, 10),
      });
      // ai_results[0] is the ruleset-winning rate. If empty, still show something.
      const winner = (data.ai_results || [])[0];
      setSmart({ winner, all: data.ai_results || [], ruleset: data.ruleset_description });
      if (!winner) toast.info("No AI recommendation — ruleset returned zero matches");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Smart Rate call failed");
    } finally {
      setSLoad(false);
    }
  }, [ruleset, origin, dest, parcel]);

  const addInsurance = async () => {
    const amt = Number(insAmount);
    if (!amt || amt <= 0) return toast.error("Insurance amount must be > 0");
    setInsLoad(true);
    try {
      let data;
      // Prefer the "insure existing EP shipment" path when we have an EP
      // shipment id; fall back to standalone insurance-by-tracking-code
      // for legacy Warp / non-EP labels.
      if (shipmentId) {
        const r = await api.post(
          `/dropship/verqo-shipping/shipments/${shipmentId}/insure`,
          { amount: amt },
        );
        data = r.data;
      } else if (trackingCode) {
        const r = await api.post("/dropship/verqo-shipping/insurance", {
          tracking_code: trackingCode,
          amount:        amt,
          carrier:       carrier || undefined,
          reference:     reference || undefined,
        });
        data = r.data;
      } else {
        toast.error("Missing tracking number or shipment id");
        return;
      }
      toast.success(`Parcel insured for $${amt.toFixed(2)}`);
      setInsDone(true);
      onInsured?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Insurance failed");
    } finally {
      setInsLoad(false);
    }
  };

  // ─── Post-book mode: Insurance only ─────────────────────────────
  if (mode === "booked") {
    if (!shipmentId && !trackingCode) return null;
    return (
      <div className="rounded-xl border border-border bg-white p-3" data-testid="verqo-post-book-insurance">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-emerald-600" />
          <div className="text-sm font-medium">Add parcel insurance</div>
          <span className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">up to $5k</span>
        </div>
        {insDone ? (
          <div className="text-sm text-emerald-700">
            ✓ Insured for ${Number(insAmount).toFixed(2)}. Claim payable if lost / damaged / stolen.
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 text-sm text-foreground/70">$</div>
            <Input
              type="number" min="1" max="5000" step="1"
              value={insAmount}
              onChange={e => setInsAmount(e.target.value)}
              className="w-32"
              data-testid="verqo-insurance-amount"
            />
            <Button
              size="sm"
              onClick={addInsurance}
              disabled={insLoading}
              data-testid="verqo-insurance-add"
            >
              {insLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              {insLoading ? "Adding…" : "Add insurance"}
            </Button>
          </div>
        )}
        <div className="text-[11px] text-foreground/50 mt-2">
          Coverage backed by Verqo Shipping. File claims directly from your order details.
        </div>
      </div>
    );
  }

  // ─── Quote mode: Zone chip + Smart Rate CTA ─────────────────────
  if (!dev) return null;

  return (
    <div className="rounded-xl border border-border bg-gradient-to-br from-purple-50/40 to-white p-3 mb-3"
         data-testid="verqo-smart-shipping-bar">
      <div className="flex items-center gap-3 flex-wrap">
        {/* USPS Zone chip */}
        {zoneLoading && (
          <div className="text-[11px] text-foreground/50 inline-flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> Zone…
          </div>
        )}
        {!zoneLoading && zone != null && (
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-sky-100 text-sky-800 border border-sky-200 text-[11px] font-medium"
               data-testid="verqo-usps-zone-chip"
               title={`USPS Zone ${zone} · determines domestic postal rates`}>
            <MapPin className="w-3 h-3" />
            USPS Zone {zone}
          </div>
        )}

        {/* Smart Rate CTA */}
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap justify-end">
          <span className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">
            Verqo Smart Rate
          </span>
          <Button
            size="sm" variant="outline"
            onClick={runSmartRate}
            disabled={smartLoading || !ruleset?.ruleset_name}
            title={!ruleset?.ruleset_name ? "Admin must configure an AI ruleset first" : ""}
            data-testid="verqo-smart-rate-btn"
            className="h-7 px-2.5 text-[11px] border-purple-200 text-purple-700 hover:bg-purple-50"
          >
            {smartLoading
              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin"/>Analyzing…</>
              : <><Sparkles className="w-3 h-3 mr-1"/>Get AI recommendation</>}
          </Button>
        </div>
      </div>

      {/* Smart Rate result */}
      {smart?.winner && (
        <div className="mt-3 border-t border-border pt-3" data-testid="verqo-smart-rate-result">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-100 grid place-items-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-purple-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                AI picks: {smart.winner.carrier} · {smart.winner.service}
                <span className="chip bg-purple-100 border border-purple-200 text-purple-800 text-[9px]">
                  ruleset winner
                </span>
              </div>
              <div className="text-[11px] text-foreground/60 mt-1 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <TrendingDown className="w-3 h-3"/>${Number(smart.winner.rate || 0).toFixed(2)}
                </span>
                {smart.winner.predicted_deliver_days && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3"/>~{smart.winner.predicted_deliver_days} days
                  </span>
                )}
                {smart.winner.predicted_deliver_by_date && (
                  <span className="text-foreground/50">
                    est. delivery {new Date(smart.winner.predicted_deliver_by_date).toLocaleDateString()}
                  </span>
                )}
              </div>
              {smart.ruleset && (
                <div className="text-[10px] text-foreground/45 mt-1 italic">
                  Ruleset: {smart.ruleset}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
